import type { DeepReadonly, Ref } from 'vue'

export interface UseAutoPlay {
  isAutoPlaying: DeepReadonly<Ref<boolean>>
  startAutoPlay(): void
  pauseAutoPlay(): void
  resumeAutoPlay(): void
  stopAutoPlay(): void
  setDelay(option: DelayOption): void
}

export interface UseAutoPlayOptions {
  onStart?: () => void
  onCycleNext: () => void
}

export const AUTOPLAY_DELAY = 5000

export enum DelayOption {
  Short = 'short',
  Medium = 'medium',
  Long = 'long',
}

export function useAutoPlay({ onStart, onCycleNext }: UseAutoPlayOptions): UseAutoPlay {
  const isAutoPlaying = ref<boolean>(false)
  let intervalRef: NodeJS.Timeout | null = null
  const delayOptions = reactive({
    [DelayOption.Short]: {
      delay: 5000,
      active: true,
    },
    [DelayOption.Medium]: {
      delay: 10000,
      active: false,
    },
    [DelayOption.Long]: {
      delay: 15000,
      active: false,
    },
  })

  const activeDelay = computed(() => {
    return Object.values(delayOptions).find(opt => opt.active)?.delay
  })

  onUnmounted(() => {
    document.body.removeEventListener('click', stopAutoPlay)
  })

  function startAutoPlay() {
    if (isAutoPlaying.value) return

    onStart?.()

    isAutoPlaying.value = true
    intervalRef = setInterval(onCycleNext, activeDelay.value)

    setTimeout(() => {
      document.body.addEventListener('click', stopAutoPlay)
    }, 0)
  }

  function pauseAutoPlay() {
    if (intervalRef) {
      clearInterval(intervalRef)
      intervalRef = null
    }
  }

  function resumeAutoPlay() {
    if (!intervalRef && isAutoPlaying.value) {
      intervalRef = setInterval(onCycleNext, activeDelay.value)
      return onCycleNext()
    }
  }

  function stopAutoPlay() {
    if (intervalRef) {
      clearInterval(intervalRef)
      intervalRef = null
    }

    if (isAutoPlaying.value) {
      isAutoPlaying.value = false
      document.body.removeEventListener('click', stopAutoPlay)
    }
  }

  function setDelay(option: DelayOption) {
    if (!(option in delayOptions)) return
    const activeOption = Object.values(delayOptions).find(opt => opt.active)
    if (activeOption) activeOption.active = false
    delayOptions[option].active = true
  }

  return {
    isAutoPlaying: readonly(isAutoPlaying),
    stopAutoPlay,
    pauseAutoPlay,
    resumeAutoPlay,
    startAutoPlay,
    setDelay,
  }
}
